/**
 * Per-viewport icon that surfaces 2D-stack mode and its cause. Renders
 * nothing in volume mode. Native `title` carries the short reason on
 * hover; click opens the diagnostic popover. Positioning-agnostic —
 * inline-block icon + popover; hosts are responsible for placement.
 */

import React, { useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { gpuVerdictAtom } from "../../atoms/gpuVerdictAtom";
import { decideViewport } from "../../utils/pickViewportComponent";
import { type BlockerCode } from "../../utils/reconstructionBlocker";
import type { SeriesMetadata } from "../../utils/dicomMetadataLoader";
import { FaExclamationCircle } from "../../utils/icons";
import { DiagnosticBody } from "./DiagnosticBody";

interface ViewportModeBadgeProps {
  meta: SeriesMetadata | null | undefined;
}

export const ViewportModeBadge: React.FC<ViewportModeBadgeProps> = ({ meta }) => {
  const verdict = useAtomValue(gpuVerdictAtom);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (popoverRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const decision = decideViewport(meta, verdict);
  if (decision.kind === "volume") return null;

  // decideViewport guarantees reason is non-null when kind === "stack" —
  // it's the single source of truth for both routing and the user-facing
  // explanation, so a new stack trigger can't be added without a label.
  const reason = decision.reason ?? "Non-volumetric";
  const blockerCode = (meta?.reconstruction_blocker ?? null) as BlockerCode | null;
  const hoverTitle = `2D · ${reason} (click for details)`;

  return (
    <span className="relative inline-block pointer-events-auto">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          // Don't bubble to the viewport's activation handler.
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        title={hoverTitle}
        aria-label={hoverTitle}
        aria-expanded={open}
        className="text-fg-muted hover:text-fg cursor-pointer leading-none align-middle"
        data-testid="viewport-mode-badge"
      >
        <FaExclamationCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Viewport mode diagnostic"
          className="absolute bottom-full left-0 mb-2 w-72 rounded-md border border-border-strong bg-bg-2 text-fg p-3 shadow-lg z-50 pointer-events-auto"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DiagnosticBody
            verdict={verdict}
            blockerCode={blockerCode}
            header="Viewport mode: 2D (Stack)"
            reasonLine={reason}
          />
        </div>
      )}
    </span>
  );
};
